import React, { useState, useEffect, useMemo } from "react";
import Layout from "@/components/Layout";
import { Box, Grid, Typography } from "@mui/material";
import Card from "@/components/Card";
import {
  StyledBadge,
  StyledPageWrapperBox,
  BoxHeaderInner,
  StyledRightActionButton,
} from "@/components/styled";
import DashboardManagementTable, {
  DashboardManagementTableColumn,
} from "./components/DashboardManagementTable";
import SVGExportDownload from "@/assets/icons/SVGExportDownload";
import SearchInput from "@/components/SearchInput";
import TablePagination from "@/components/TablePagination";
import TableSort from "@/components/TableSort";
import { useTheme, useMediaQuery } from "@mui/material";
import { getMetrics } from "@/api/metricsApi";
import CircularProgress from "@mui/material/CircularProgress";
import { INCENTIVE_STATUS, INCENTIVE_STATUS_ITEMS } from "@/constants";
import {
  getAllProjectsReq,
  getAllProjectsToExportReq,
} from "@/api/projectsApi";

import DashboardDonutChart from "./components/DashboardDonutChart";
import StatCard from "./components/StatCard";
import { getFileStorageBadgeVariant } from "@/utils/getIncentiveBadgeVariant";
import { formatShortMonthDate } from "@/utils/formatDate.ts";
import { formatStatus } from "@/utils/formatStatus.ts";
import { formatCurrency } from "@/utils/formatCurrency.ts";
import {
  OEM_COLORS,
  CUSTOMER_TYPE_COLORS,
  RATE_TYPE_COLORS,
  contractorStatusVariant,
  oemStatusVariant,
  customerStatusVariant,
  exportToCSV,
  calculateTrend,
  calculateTrendColor,
  getOemLabel,
  readKpiValue,
  formatSharePercent,
  buildCustomerTypeRow,
} from "./helpers";
import dayjs from "dayjs";
import GeographicDistributionMap from "@/components/GeographicDistributionMap";

const DashboardPage = () => {
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<any[]>([]);

  // Pagination state for each table
  const [contractorPage, setContractorPage] = useState(0);
  const [contractorRowsPerPage, setContractorRowsPerPage] = useState(10);
  const [oemPage, setOemPage] = useState(0);
  const [oemRowsPerPage, setOemRowsPerPage] = useState(10);
  const [customerPage, setCustomerPage] = useState(0);
  const [customerRowsPerPage, setCustomerRowsPerPage] = useState(10);
  const [weeklyTrendsPage, setWeeklyTrendsPage] = useState(0);
  const [weeklyTrendsRowsPerPage, setWeeklyTrendsRowsPerPage] = useState(10);
  const [contractorSearch, setContractorSearch] = useState("");
  const [oemSearch, setOemSearch] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [geoMapSearch, setGeoMapSearch] = useState("");

  // Handle geographic distribution CSV export
  const handleGeoDistributionExport = async () => {
    try {
      const response = await getAllProjectsToExportReq(geoMapSearch);

      if (!response.data || response.data.length === 0) {
        console.log("No data to export");
        return;
      }

      // Group by city and calculate metrics (if needed)
      // For now, just export the projects data
      exportToCSV(response.data, "geographic-distribution-projects");
    } catch (error) {
      console.error("Failed to export geographic distribution:", error);
    }
  };

  // Sort state for each table
  const [contractorSort, setContractorSort] = useState("");
  const [oemSort, setOemSort] = useState("");
  const [customerSort, setCustomerSort] = useState("");
  const [customerStatusSort, setCustomerStatusSort] = useState("");

  // Handlers for pagination
  const handleContractorChangePage = (_: unknown, newPage: number) =>
    setContractorPage(newPage);
  const handleContractorChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setContractorRowsPerPage(Number.parseInt(e.target.value, 10));
    setContractorPage(0);
  };
  const handleOemChangePage = (_: unknown, newPage: number) =>
    setOemPage(newPage);
  const handleOemChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOemRowsPerPage(Number.parseInt(e.target.value, 10));
    setOemPage(0);
  };
  const handleCustomerChangePage = (_: unknown, newPage: number) =>
    setCustomerPage(newPage);
  const handleCustomerChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCustomerRowsPerPage(Number.parseInt(e.target.value, 10));
    setCustomerPage(0);
  };
  const handleWeeklyTrendsChangePage = (_: unknown, newPage: number) =>
    setWeeklyTrendsPage(newPage);
  const handleWeeklyTrendsChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setWeeklyTrendsRowsPerPage(Number.parseInt(e.target.value, 10));
    setWeeklyTrendsPage(0);
  };

  // Handlers for search
  const handleContractorSearch = (value: string) => setContractorSearch(value);
  const handleOemSearch = (value: string) => setOemSearch(value);
  const handleCustomerSearch = (value: string) => setCustomerSearch(value);

  // Export to CSV handlers
  const handleExportContractorCSV = () => {
    const data = metrics?.contractorMetrics || [];
    exportToCSV(data, "contractor_metrics");
  };

  const handleExportOemCSV = () => {
    const data = metrics?.oemMetrics || [];
    exportToCSV(data, "oem_metrics");
  };

  const handleExportCustomerCSV = () => {
    const data = metrics?.nemDistribution || [];
    exportToCSV(data, "customer_type_breakdown");
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [metricsData, projectsData] = await Promise.all([
          getMetrics(),
          getAllProjectsReq("1", 1000, "", [], false, ""), // Get all projects for incentive calculations
        ]);
        setMetrics(metricsData);
        setProjects(projectsData?.data?.projects || []);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setContractorPage(0);
  }, [contractorSearch, contractorSort]);

  useEffect(() => {
    setOemPage(0);
  }, [oemSearch, oemSort]);

  useEffect(() => {
    setCustomerPage(0);
  }, [customerSearch, customerStatusSort]);

  // Normalize all KPI values to { value, percentChange }
  const kpis = metrics?.kpis || {};
  const normalizedKpis: Record<
    string,
    { value: number | string; percentChange: number }
  > = {};
  for (const key in kpis) {
    if (
      typeof kpis[key] === "object" &&
      kpis[key] !== null &&
      "value" in kpis[key]
    ) {
      normalizedKpis[key] = kpis[key];
    } else {
      normalizedKpis[key] = { value: kpis[key], percentChange: 0 };
    }
  }

  const statusBadge = (status: string) => {
    // Find the label and color from INCENTIVE_STATUS_ITEMS
    const statusItem = INCENTIVE_STATUS_ITEMS.find(
      (item) => item.label === status || item.value === status
    );
    const badgeVariant = getFileStorageBadgeVariant(status);
    return (
      <StyledBadge
        {...badgeVariant}
        sx={{
          fontWeight: 600,
          fontSize: 14,
          px: 2,
          py: 0.5,
          minWidth: "120px",
          display: "inline-block",
          textAlign: "center",
        }}
      >
        {statusItem ? statusItem.label : status}
      </StyledBadge>
    );
  };

  // Table column definitions
  const contractorColumns: DashboardManagementTableColumn[] = [
    { label: "Contractor Name", field: "contractorName" },
    {
      label: "Status",
      field: "status",
      sortable: true,
      render: (status: string) => (
        <StyledBadge
          sx={{ display: "inline", textWrap: "nowrap" }}
          {...contractorStatusVariant(status)}
        >
          {formatStatus(status)}
        </StyledBadge>
      ),
    },
    { label: "Projects Submitted", field: "projectsSubmitted" },
    { label: "Projects Approved", field: "projectsApproved", sortable: true },
    { label: "% Approved (Approval Rate)", field: "percentApproved" },
    {
      label: "Existing Solar - Market Rate",
      field: "existingSolarMarketRate",
      sortable: true,
    },
    {
      label: "Existing Solar - Non-Market Rate",
      field: "existingSolarNonMarketRate",
      sortable: true,
    },
    {
      label: "No Solar - Market Rate",
      field: "noSolarMarketRate",
      sortable: true,
    },
    {
      label: "No Solar - Non-Market Rate",
      field: "noSolarNonMarketRate",
      sortable: true,
    },
    {
      label: "Approved Incentives - Market Rate",
      field: "approvedIncentivesMarketRate",
      sortable: true,
    },
    {
      label: "Approved Incentives - Non-Market Rate",
      field: "approvedIncentivesNonMarketRate",
      sortable: true,
    },
    {
      label: "Total Approved Incentives",
      field: "totalApprovedIncentives",
      sortable: true,
    },
    {
      label: "% of Total Incentives",
      field: "percentOfTotalIncentives",
      sortable: true,
    },
  ];

  const oemColumns: DashboardManagementTableColumn[] = [
    { label: "OEM Name", field: "fullName" },
    { label: "OEM Product", field: "oemProduct" },
    { label: "Projects Approved", field: "projectsApproved", sortable: true },
    {
      label: "Status",
      field: "status",
      sortable: true,
      render: (status: string) => (
        <StyledBadge
          sx={{ display: "inline", textWrap: "nowrap" }}
          {...oemStatusVariant(status)}
        >
          {formatStatus(status)}
        </StyledBadge>
      ),
    },
  ];

  const customerColumns: DashboardManagementTableColumn[] = [
    { label: "Rate Type", field: "fullName" },
    { label: "Market Rate", field: "marketRate", sortable: true },
    { label: "Non-Market Rate", field: "nonMarketRate", sortable: true },
    {
      label: "Status",
      field: "status",
      sortable: true,
      render: (status: string) => (
        <StyledBadge
          sx={{ display: "inline", textWrap: "nowrap" }}
          {...customerStatusVariant(status)}
        >
          {formatStatus(status)}
        </StyledBadge>
      ),
    },
  ];

  // Sort options for each table
  const contractorSortOptions = [
    { value: "contractorName", label: "Contractor Name" },
    { value: "status", label: "Status" },
    { value: "projectsApproved", label: "Projects Approved" },
    { value: "projectsSubmitted", label: "Projects Submitted" },
    { value: "percentApproved", label: "% Approved" },
  ];

  const contractorStatusOptions = [
    { value: "pending_approval", label: "Pending Approval" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "deleted", label: "Deleted" },
  ];

  const oemSortOptions = [
    { value: "fullName", label: "OEM Name" },
    { value: "oemProduct", label: "OEM Product" },
    { value: "projectsApproved", label: "Projects Approved" },
    { value: "status", label: "Status" },
  ];

  const oemStatusOptions = [
    { value: "pending_approval", label: "Pending Approval" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "deleted", label: "Deleted" },
  ];

  const customerSortOptions = [
    { value: "fullName", label: "Rate Type" },
    { value: "marketRate", label: "Market Rate" },
    { value: "nonMarketRate", label: "Non-Market Rate" },
    { value: "status", label: "Status" },
  ];

  const customerStatusOptions = [
    { value: "Active", label: "Active" },
    { value: "Pending", label: "Pending" },
    { value: "Rejected", label: "Rejected" },
    { value: "Inactive", label: "Inactive" },
  ];

  // Weekly trends columns
  const weeklyTrendsColumns: DashboardManagementTableColumn[] = [
    { label: "Week", field: "week" },
    { label: "Projects Submitted", field: "projectsSubmitted" },
    { label: "Projects Approved", field: "projectsApproved", sortable: true },
    {
      label: "% Approved (Approval Rate)",
      field: "approvalRate",
      sortable: true,
    },
  ];

  // Process table data
  const allFilteredContractorRows = useMemo(() => {
    try {
      const contractorData = metrics?.contractorMetrics || [];
      if (!Array.isArray(contractorData)) return [];

      // SDCP-1308: "% of Total Incentives" is each row's share of the incentive total across
      // all contractors (computed over the full set so it is stable under search/filter).
      const grandTotalIncentives = contractorData.reduce(
        (sum: number, it: { totalIncentives?: number | string }) =>
          sum + Number(it?.totalIncentives || 0),
        0
      );

      return contractorData
        .filter((item: any) => {
          if (!item || typeof item !== "object") return false;

          // Filter by search term
          const contractorName =
            item.Contractor?.companyName || `Contractor ${item.contractorId}`;
          const searchMatch = contractorName
            .toLowerCase()
            .includes(contractorSearch.toLowerCase());

          // Filter by status if selected
          const actualStatus = item.Contractor?.status || "unknown";
          const statusMatch =
            !contractorSort || contractorSort === actualStatus;

          return searchMatch && statusMatch;
        })
        .map((item: any) => {
          const approved = Number(item.approved || 0);
          const submitted = Number(item.submitted || 0);
          const percentApproved =
            submitted > 0 ? ((approved / submitted) * 100).toFixed(1) : "0.0";

          // Use actual status from API
          const actualStatus = item.Contractor?.status || "unknown";

          return {
            contractorName:
              item.Contractor?.companyName || `Contractor ${item.contractorId}`,
            status: actualStatus,
            projectsApproved: approved,
            projectsSubmitted: submitted,
            percentApproved: `${percentApproved}%`,
            existingSolarMarketRate: item.marketRate || 0,
            existingSolarNonMarketRate: item.nonMarketRate || 0,
            noSolarMarketRate: 0, // Not provided in API
            noSolarNonMarketRate: 0, // Not provided in API
            // SDCP-1308: the dashboard API now converts cents to dollars once, in
            // dashboard.service. Dividing again here would put these back off by 100.
            approvedIncentivesMarketRate: formatCurrency(
              Number(item.marketRateIncentives || 0)
            ),
            approvedIncentivesNonMarketRate: formatCurrency(
              Number(item.nonMarketRateIncentives || 0)
            ),
            totalApprovedIncentives: formatCurrency(
              Number(item.totalIncentives || 0)
            ),
            percentOfTotalIncentives: formatSharePercent(
              item.totalIncentives,
              grandTotalIncentives
            ),
          };
        });
    } catch (error) {
      console.error("Error processing contractor rows:", error);
      return [];
    }
  }, [metrics, contractorSearch, contractorSort]);

  const filteredContractorRows = useMemo(
    () =>
      allFilteredContractorRows.slice(
        contractorPage * contractorRowsPerPage,
        contractorPage * contractorRowsPerPage + contractorRowsPerPage
      ),
    [allFilteredContractorRows, contractorPage, contractorRowsPerPage]
  );

  const allFilteredOemRows = useMemo(() => {
    try {
      const oemData = metrics?.oemMetrics || [];
      if (!Array.isArray(oemData)) return [];

      return oemData
        .filter((item: any) => {
          if (!item || typeof item !== "object") return false;

          // Filter by search term
          const oemName = getOemLabel(item);
          const searchMatch = oemName
            .toLowerCase()
            .includes(oemSearch.toLowerCase());

          // Filter by status if selected
          const actualStatus = item.OEM?.status || "unknown";
          const statusMatch = !oemSort || oemSort === actualStatus;

          return searchMatch && statusMatch;
        })
        .map((item: any) => {
          const approvedProjects = Number(item.approvedProjects || 0);
          // Use actual status from API
          const actualStatus = item.OEM?.status || "unknown";

          return {
            fullName: getOemLabel(item),
            oemProduct: item.productModels || "N/A",
            projectsApproved: approvedProjects,
            status: actualStatus,
          };
        });
    } catch (error) {
      console.error("Error processing OEM rows:", error);
      return [];
    }
  }, [metrics, oemSearch, oemSort]);

  const filteredOemRows = useMemo(
    () =>
      allFilteredOemRows.slice(
        oemPage * oemRowsPerPage,
        oemPage * oemRowsPerPage + oemRowsPerPage
      ),
    [allFilteredOemRows, oemPage, oemRowsPerPage]
  );

  const allFilteredCustomerRows = useMemo(() => {
    try {
      const customerData = metrics?.nemDistribution || [];
      if (!Array.isArray(customerData)) return [];

      return customerData
        .filter((item: any) => {
          if (!item || typeof item !== "object") return false;

          // Filter by search term
          const rateType = item.rateType || "";
          const searchMatch = rateType
            .toLowerCase()
            .includes(customerSearch.toLowerCase());

          // Determine status based on actual data
          let status = "Inactive";
          if (item.activeCount > 0) {
            status = "Active";
          } else if (item.pendingCount > 0) {
            status = "Pending";
          } else if (item.rejectedCount > 0) {
            status = "Rejected";
          }

          // Filter by status if selected
          const statusMatch =
            !customerStatusSort || customerStatusSort === status;

          return searchMatch && statusMatch;
        })
        .map((item: any) => {
          // Determine status based on actual data
          let status = "Inactive";
          if (item.activeCount > 0) {
            status = "Active";
          } else if (item.pendingCount > 0) {
            status = "Pending";
          } else if (item.rejectedCount > 0) {
            status = "Rejected";
          }

          // SDCP-1308: label carries the tariff (so NEM/None rows of the same rate type are
          // distinct) and the count keys off rate type alone (so a None + Market Rate row no
          // longer falls through every branch and renders an empty 0/0 row).
          const { fullName, marketRate, nonMarketRate } =
            buildCustomerTypeRow(item);

          return {
            fullName,
            marketRate,
            nonMarketRate,
            status: status,
          };
        });
    } catch (error) {
      console.error("Error processing customer rows:", error);
      return [];
    }
  }, [metrics, customerSearch, customerStatusSort]);

  const filteredCustomerRows = useMemo(
    () =>
      allFilteredCustomerRows.slice(
        customerPage * customerRowsPerPage,
        customerPage * customerRowsPerPage + customerRowsPerPage
      ),
    [allFilteredCustomerRows, customerPage, customerRowsPerPage]
  );

  const allWeeklyTrendsRows = useMemo(() => {
    try {
      const weeklyData = metrics?.weeklyTrends || [];
      if (!Array.isArray(weeklyData)) return [];

      return weeklyData.map((item: any) => {
        const submitted = Number(item.submitted || 0);
        const approved = Number(item.approved || 0);
        const approvalRate =
          submitted > 0 ? ((approved / submitted) * 100).toFixed(0) : "0";

        // Format the week date
        const formattedWeek = formatShortMonthDate(item.week);

        return {
          week: formattedWeek,
          projectsSubmitted: submitted,
          projectsApproved: approved,
          approvalRate: `${approvalRate}%`,
        };
      });
    } catch (error) {
      console.error("Error processing weekly trends rows:", error);
      return [];
    }
  }, [metrics]);

  const weeklyTrendsRows = useMemo(
    () =>
      allWeeklyTrendsRows.slice(
        weeklyTrendsPage * weeklyTrendsRowsPerPage,
        weeklyTrendsPage * weeklyTrendsRowsPerPage + weeklyTrendsRowsPerPage
      ),
    [allWeeklyTrendsRows, weeklyTrendsPage, weeklyTrendsRowsPerPage]
  );

  // Calculate incentive and project metrics
  const incentiveMetrics = (() => {
    try {
      let incentiveApproved = 0;
      let incentivePaid = 0;
      let projectApproved = 0;
      let projectInstalled = 0;

      if (Array.isArray(projects)) {
        projects.forEach((project: any) => {
          const upfrontIncentivePayment = Number(
            project.upfrontIncentivePayment || 0
          );

          // Incentive calculations
          if (project.incentiveStatus === INCENTIVE_STATUS.RESERVED) {
            incentiveApproved += upfrontIncentivePayment;
            projectApproved += 1; // Count projects with Upfront-Reserved status
          } else if (project.incentiveStatus === INCENTIVE_STATUS.PAID) {
            incentivePaid += upfrontIncentivePayment;
          }

          // Project installed calculation
          // Check if project has PTO letter (operationalizedDate) or is in PAID status
          if (
            project.operationalizedDate ||
            project.incentiveStatus === INCENTIVE_STATUS.PAID
          ) {
            projectInstalled += 1;
          }
        });
      }

      return {
        // These come from the raw projects list, not the dashboard API, so they are still in
        // cents and this division stays (SDCP-1308).
        incentiveApproved: incentiveApproved / 100,
        incentivePaid: incentivePaid / 100,
        projectApproved: projectApproved,
        projectInstalled: projectInstalled,
      };
    } catch (error) {
      console.error("Error calculating incentive metrics:", error);
      return {
        incentiveApproved: 0,
        incentivePaid: 0,
        projectApproved: 0,
        projectInstalled: 0,
      };
    }
  })();

  // Map oemMetrics for donut chart
  const oemChartData = (metrics?.oemMetrics || []).map((item: any) => ({
    count: Number(item.approvedProjects ?? 0),
    name: getOemLabel(item),
    color: item.OEM?.color ?? "#E2E2E2",
  }));

  // Function to generate comparison period dates
  const getComparisonPeriod = () => {
    const endDate = dayjs().subtract(1, "week").endOf("week"); // Last Sunday
    const startDate = endDate.startOf("week"); // Last Monday
    return `vs ${startDate.format("MMM D")}–${endDate.format("D, YYYY")}`;
  };

  const renderStatCard = (
    value: string | number,
    label: string,
    percentChange: number | undefined,
    defaultTrendColor: "green" | "red" = "green",
    trendTooltip?: {
      description: string;
      comparisonPeriod: string;
    }
  ) => (
    <StatCard
      value={value}
      label={label}
      trend={calculateTrend(percentChange)}
      trendColor={calculateTrendColor(percentChange, defaultTrendColor)}
      trendTooltip={trendTooltip}
      sx={{ borderRadius: "16px" }}
    />
  );

  if (loading || !metrics) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Layout>
      <StyledPageWrapperBox>
        <Box sx={{ width: "100%" }}>
          {/* Top Row: Active Projects (full width) */}
          <Grid container spacing={3} sx={{ marginBottom: 3 }}>
            <Grid item xs={12}>
              <StatCard
                value={normalizedKpis.activeProjects?.value || 0}
                label="Active Projects"
                trend="+2.7%"
                trendColor="green"
                trendTooltip={{
                  description:
                    "Data trends on a weekly basis, with comparisons made from Monday to Sunday.",
                  comparisonPeriod: getComparisonPeriod(),
                }}
                sx={{ borderRadius: "16px" }}
              />
            </Grid>
          </Grid>

          {/* Middle Row: 4 cards - Approved metrics */}
          <Grid container spacing={3} sx={{ marginBottom: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              {renderStatCard(
                normalizedKpis.totalProjectsApproved?.value || 0,
                "Total Projects Approved",
                normalizedKpis.totalProjectsApproved?.percentChange,
                "green",
                {
                  description:
                    "Project approval trends tracked weekly with Monday to Sunday comparisons.",
                  comparisonPeriod: getComparisonPeriod(),
                }
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderStatCard(
                formatCurrency(
                  Number(normalizedKpis.totalIncentivesApproved?.value) || 0
                ),
                "Total Incentives Approved",
                normalizedKpis.totalIncentivesApproved?.percentChange,
                "green",
                {
                  description:
                    "Incentive approval amounts tracked weekly with Monday to Sunday comparisons.",
                  comparisonPeriod: getComparisonPeriod(),
                }
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderStatCard(
                normalizedKpis.totalStorageApproved?.value || 0,
                "Total Storage Capacity Approved",
                normalizedKpis.totalStorageApproved?.percentChange,
                "green",
                {
                  description:
                    "Storage capacity approval trends tracked weekly with Monday to Sunday comparisons.",
                  comparisonPeriod: getComparisonPeriod(),
                }
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderStatCard(
                normalizedKpis.totalSolarApproved?.value || 0,
                "Total Solar Capacity Approved",
                normalizedKpis.totalSolarApproved?.percentChange,
                "green",
                {
                  description:
                    "Solar capacity approval trends tracked weekly with Monday to Sunday comparisons.",
                  comparisonPeriod: getComparisonPeriod(),
                }
              )}
            </Grid>
          </Grid>

          {/* Bottom Row: 4 cards - Installed metrics */}
          <Grid container spacing={3} sx={{ marginBottom: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              {renderStatCard(
                normalizedKpis.totalProjectsInstalled?.value || 0,
                "Total Projects Installed",
                normalizedKpis.totalProjectsInstalled?.percentChange,
                "green",
                {
                  description:
                    "Project installation trends tracked weekly with Monday to Sunday comparisons.",
                  comparisonPeriod: getComparisonPeriod(),
                }
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderStatCard(
                // SDCP-1308: the API emits totalAmountPaid as a plain number of dollars, but
                // has shipped both shapes; read .value only when it is actually an object so a
                // legitimate 0 does not fall through to the wrapper and render as NaN.
                formatCurrency(readKpiValue(normalizedKpis.totalAmountPaid)),
                "Total Amount Paid",
                normalizedKpis.totalAmountPaid?.percentChange,
                "red",
                {
                  description:
                    "Incentive payment trends tracked weekly with Monday to Sunday comparisons.",
                  comparisonPeriod: getComparisonPeriod(),
                }
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderStatCard(
                normalizedKpis.totalStorageInstalled?.value || 0,
                "Total Storage Capacity Installed",
                normalizedKpis.totalStorageInstalled?.percentChange,
                "green",
                {
                  description:
                    "Storage capacity installation trends tracked weekly with Monday to Sunday comparisons.",
                  comparisonPeriod: getComparisonPeriod(),
                }
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderStatCard(
                normalizedKpis.totalSolarInstalled?.value || 0,
                "Total Solar Capacity Installed",
                normalizedKpis.totalSolarInstalled?.percentChange,
                "green",
                {
                  description:
                    "Solar capacity installation trends tracked weekly with Monday to Sunday comparisons.",
                  comparisonPeriod: getComparisonPeriod(),
                }
              )}
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ marginBottom: 4 }}>
            <Grid item xs={12} md={6} lg={3}>
              <DashboardDonutChart
                title="OEM Project Distribution"
                data={(metrics?.oemMetrics || []).map(
                  (item: any, idx: number) => ({
                    count: Number(item.approvedProjects ?? 0),
                    name: getOemLabel(item),
                    color: OEM_COLORS[idx % OEM_COLORS.length],
                  })
                )}
                valueField="count"
                labelField="name"
                colorField="color"
                isCurrency={false}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <DashboardDonutChart
                title="OEM Distribution Based on Incentives"
                data={(metrics?.oemIncentiveDistribution || []).map(
                  (item: any, idx: number) => ({
                    amount: Number(item.totalIncentives || 0),
                    name: getOemLabel(item),
                    color: OEM_COLORS[idx % OEM_COLORS.length],
                  })
                )}
                valueField="amount"
                labelField="name"
                colorField="color"
                isCurrency={true}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <DashboardDonutChart
                title="Project Distribution by Customer Type"
                data={(metrics?.nemDistribution || []).map((item: any) => ({
                  count: Number(item.count || 0),
                  type: `${item.tariffType} - ${item.rateType}`,
                  color:
                    CUSTOMER_TYPE_COLORS[
                      item.rateType as keyof typeof CUSTOMER_TYPE_COLORS
                    ] || "#E2E2E2",
                }))}
                valueField="count"
                labelField="type"
                colorField="color"
                isCurrency={false}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <DashboardDonutChart
                title="Incentive Distribution by Customer Type"
                data={(metrics?.marketRateDistribution || []).map(
                  (item: any) => ({
                    amount: Number(item.incentives || 0),
                    type: item.rateType || "Unknown",
                    color:
                      RATE_TYPE_COLORS[
                        item.rateType as keyof typeof RATE_TYPE_COLORS
                      ] || "#E2E2E2",
                  })
                )}
                valueField="amount"
                labelField="type"
                colorField="color"
                isCurrency={true}
              />
            </Grid>
          </Grid>

          {/* Contractor Activity Summary Table */}
          <Card
            sx={{
              marginBottom: 4,
              height: 650,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography variant="cardHeader2" sx={{ mb: 2 }}>
              Contractor Activity Summary
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box sx={{ width: 240 }}>
                  <SearchInput onDebounce={handleContractorSearch} />
                </Box>
                <TableSort
                  placeholder="All statuses"
                  items={contractorStatusOptions}
                  setSortValue={setContractorSort}
                />
              </Box>
              <StyledRightActionButton
                onClick={handleExportContractorCSV}
                sx={{ gap: "8px", whiteSpace: "nowrap" }}
                variant="contained"
              >
                <SVGExportDownload color="white" size={18} />
                {!downSm && "Export to CSV"}
              </StyledRightActionButton>
            </Box>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <DashboardManagementTable
                columns={contractorColumns}
                rows={filteredContractorRows}
                pagination={
                  <TablePagination
                    rowsPerPage={contractorRowsPerPage}
                    page={contractorPage}
                    handleChangePage={handleContractorChangePage}
                    handleChangeRowsPerPage={handleContractorChangeRowsPerPage}
                    totalCount={allFilteredContractorRows.length}
                  />
                }
              />
            </Box>
          </Card>

          {/* OEM and Customer Tables Row */}
          <Grid container spacing={2} sx={{ marginBottom: 4 }}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: 650,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Typography variant="cardHeader2" sx={{ mb: 2 }}>
                  OEM Product Approvals
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Box sx={{ width: 240 }}>
                      <SearchInput onDebounce={handleOemSearch} />
                    </Box>
                    <TableSort
                      placeholder="All statuses"
                      items={oemStatusOptions}
                      setSortValue={setOemSort}
                    />
                  </Box>
                  <StyledRightActionButton
                    onClick={handleExportOemCSV}
                    sx={{ gap: "8px", whiteSpace: "nowrap" }}
                    variant="contained"
                  >
                    <SVGExportDownload color="white" size={18} />
                    {!downSm && "Export to CSV"}
                  </StyledRightActionButton>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflow: "hidden",
                  }}
                >
                  <DashboardManagementTable
                    columns={oemColumns}
                    rows={filteredOemRows}
                    pagination={
                      <TablePagination
                        rowsPerPage={oemRowsPerPage}
                        page={oemPage}
                        handleChangePage={handleOemChangePage}
                        handleChangeRowsPerPage={handleOemChangeRowsPerPage}
                        totalCount={allFilteredOemRows.length}
                      />
                    }
                  />
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: 650,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Typography variant="cardHeader2" sx={{ mb: 2 }}>
                  Customer Type Breakdown
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Box sx={{ width: 180 }}>
                      <SearchInput onDebounce={handleCustomerSearch} />
                    </Box>
                    <TableSort
                      placeholder="All statuses"
                      items={customerStatusOptions}
                      setSortValue={setCustomerStatusSort}
                    />
                    <TableSort
                      placeholder="All Rate Types"
                      items={customerSortOptions}
                      setSortValue={setCustomerSort}
                    />
                  </Box>
                  <StyledRightActionButton
                    onClick={handleExportCustomerCSV}
                    sx={{ gap: "8px", whiteSpace: "nowrap" }}
                    variant="contained"
                  >
                    <SVGExportDownload color="white" size={18} />
                    {!downSm && "Export to CSV"}
                  </StyledRightActionButton>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflow: "hidden",
                  }}
                >
                  <DashboardManagementTable
                    columns={customerColumns}
                    rows={filteredCustomerRows}
                    pagination={
                      <TablePagination
                        rowsPerPage={customerRowsPerPage}
                        page={customerPage}
                        handleChangePage={handleCustomerChangePage}
                        handleChangeRowsPerPage={
                          handleCustomerChangeRowsPerPage
                        }
                        totalCount={allFilteredCustomerRows.length}
                      />
                    }
                  />
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Bottom Row: Weekly Trends and Geographic Distribution */}
          <Grid container spacing={2} sx={{ marginBottom: 4 }}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: 650,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Typography variant="cardHeader2" sx={{ mb: 2 }}>
                  Weekly Trends
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box sx={{ width: 240 }}>
                    <SearchInput onDebounce={() => {}} />
                  </Box>
                  <StyledRightActionButton
                    onClick={() => {}}
                    sx={{ gap: "8px", whiteSpace: "nowrap" }}
                    variant="contained"
                  >
                    <SVGExportDownload color="white" size={18} />
                    {!downSm && "Export to CSV"}
                  </StyledRightActionButton>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflow: "hidden",
                  }}
                >
                  <DashboardManagementTable
                    columns={weeklyTrendsColumns}
                    rows={weeklyTrendsRows}
                    pagination={
                      <TablePagination
                        rowsPerPage={weeklyTrendsRowsPerPage}
                        page={weeklyTrendsPage}
                        handleChangePage={handleWeeklyTrendsChangePage}
                        handleChangeRowsPerPage={
                          handleWeeklyTrendsChangeRowsPerPage
                        }
                        totalCount={allWeeklyTrendsRows.length}
                      />
                    }
                  />
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: 650,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Typography variant="cardHeader2" sx={{ mb: 2 }}>
                  Geographic Distribution of Projects
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box sx={{ width: 240 }}>
                    <SearchInput
                      onDebounce={(value) => setGeoMapSearch(value)}
                    />
                  </Box>
                  <StyledRightActionButton
                    onClick={handleGeoDistributionExport}
                    sx={{ gap: "8px", whiteSpace: "nowrap" }}
                    variant="contained"
                  >
                    <SVGExportDownload color="white" size={18} />
                    {!downSm && "Export to CSV"}
                  </StyledRightActionButton>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                  }}
                >
                  <GeographicDistributionMap search={geoMapSearch} />
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </StyledPageWrapperBox>
      <BoxHeaderInner />
    </Layout>
  );
};

export default DashboardPage;
